/**
 * PolarStarBanner — Banner "stella polare" mostrato in cima a WeeklyPlanChat.
 *
 * Serve a mantenere ancorata la pianificazione settimanale a:
 *   1) Il tema/perché dell'anno in corso — la "stella polare" più alta
 *   2) Il KR con il maggior scostamento negativo del mese — il "gap da chiudere"
 *
 * Riceve `stato` (payload di `fetchStateSettimana`) e lo interpreta.
 * Renderizza nulla se manca il tema anno E non ci sono KR in scostamento
 * (per non aggiungere rumore visivo su un anno appena creato).
 */
import { Compass, TrendingDown } from 'lucide-react';

export default function PolarStarBanner({ stato }) {
  const anno = stato?.anno || {};
  const tema = (anno.tema || '').trim();
  const perche = (anno.perche || '').trim();
  const diff = Array.isArray(stato?.diff_mese) ? stato.diff_mese : [];

  // KR peggiore: `delta_pct` negativo più grande in modulo tra i non-on_track
  const worst = diff
    .filter((d) => d && d.on_track === false)
    .sort((a, b) => Math.abs(b.delta_pct || 0) - Math.abs(a.delta_pct || 0))[0];

  if (!tema && !worst) return null;

  return (
    <div
      className="border-l-2 border-accent bg-accent/5 p-4 mb-4 space-y-2"
      data-testid="polar-star-banner"
    >
      {tema && (
        <div className="flex items-start gap-3">
          <Compass size={14} strokeWidth={1.6} className="text-accent mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mb-1">
              Stella polare · {anno.anno || 'Anno'}
            </div>
            <div className="font-display text-base text-ink leading-snug">
              {tema}
            </div>
            {perche && (
              <div className="font-body text-xs text-soft mt-1 leading-snug italic">
                {perche}
              </div>
            )}
          </div>
        </div>
      )}
      {worst && (
        <div className="flex items-start gap-3 pt-1">
          <TrendingDown size={14} strokeWidth={1.6} className="text-mute mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute mb-1">
              KR in ritardo mese · gap da chiudere
            </div>
            <div className="font-body text-sm text-ink leading-snug">
              <span className="font-medium">{worst.titolo}</span>
              {worst.reale != null && worst.atteso != null && (
                <>
                  {' '}<span className="text-soft tabular-nums font-mono text-xs">
                    {formatNum(worst.reale, worst.unita)} / {formatNum(worst.atteso, worst.unita)} atteso
                  </span>
                </>
              )}
              {worst.delta_pct != null && (
                <span className="text-accent font-mono text-xs ml-2 tabular-nums">
                  {worst.delta_pct > 0 ? '+' : ''}{worst.delta_pct}%
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatNum(v, unita) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Number.isInteger(n)
    ? n.toLocaleString('it-IT')
    : n.toLocaleString('it-IT', { maximumFractionDigits: 1 });
  return unita ? `${s} ${unita}` : s;
}
